import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPrinciples,
  setCurrentPage,
  setSelectedPrinciple,
  addPrinciple,
  updatePrinciple,
  deletePrinciple,
} from '../../redux/study/principleSlice';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Principles = () => {
  const dispatch = useDispatch();

  const {
    principles = [],
    totalPages = 1,
    currentPage = 1,
    loading = false,
    error = null,
    selectedPrinciple = null,
  } = useSelector((state) => state.principles || {});

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [newPrinciple, setNewPrinciple] = useState({
    title: '',
    description: '',
    phase: 'All',
  });

  const principlesPerPage = 5;
  const maxPageNumbers = 5;

  useEffect(() => {
    dispatch(fetchPrinciples({ search: searchTerm, page: currentPage, limit: principlesPerPage }));
  }, [dispatch, searchTerm, currentPage]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      dispatch(setCurrentPage(pageNumber));
    }
  };

  const openAddModal = () => {
    setNewPrinciple({ title: '', description: '', phase: 'All' });
    setShowAddModal(true);
  };

  const openEditModal = (principle) => {
    dispatch(setSelectedPrinciple(principle));
    setShowEditModal(true);
  };

  const handleAddPrinciple = async () => {
    try {
      await dispatch(addPrinciple(newPrinciple));
      dispatch(fetchPrinciples({ search: searchTerm, page: currentPage, limit: principlesPerPage }));
      setShowAddModal(false);
      toastr.success('Principle added successfully!');
    } catch (error) {
      console.error('Error adding principle:', error);
      toastr.error('Error adding principle!');
    }
  };

  const handleUpdatePrinciple = async () => {
    if (selectedPrinciple) {
      try {
        await dispatch(updatePrinciple({
          id: selectedPrinciple.id,
          principleData: selectedPrinciple
        }));
        dispatch(fetchPrinciples({ search: searchTerm, page: currentPage, limit: principlesPerPage }));
        setShowEditModal(false);
        toastr.success('Principle updated successfully!');
      } catch (error) {
        console.error('Error updating principle:', error);
        toastr.error('Error updating principle!');
      }
    }
  };

  const handleDeletePrinciple = async (id) => {
    try {
      await dispatch(deletePrinciple(id));
      dispatch(fetchPrinciples({ search: searchTerm, page: currentPage, limit: principlesPerPage }));
      toastr.success('Principle deleted successfully!');
    } catch (error) {
      console.error('Error deleting principle:', error);
      toastr.error('Error deleting principle!');
    }
  };

  const pageNumbers = [];
  const startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
  const endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <section className="content">
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h3 className="card-title mb-0 mr-2">Principles List</h3>
              <button
                className="btn btn-success btn-sm rounded-circle"
                style={{ width: '30px', height: '30px', padding: 0 }}
                onClick={openAddModal}
                title="Add Principle"
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search Principle"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ maxWidth: '200px' }}
            />
          </div>

          <div className="card-body p-0">
            {loading && (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
            {error && <p className="text-center text-danger">Error: {error}</p>}

            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Phase</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Date</th>
                  <th className="text-center" style={{ width: '100px' }} >Actions</th>
                </tr>
              </thead>
              <tbody>
                {principles.map((principle) => (
                  <tr key={principle.id}>
                    <td>{principle.id}</td>
                    <td>{principle.phase}</td>
                    <td>{principle.title}</td>
                    <td>{principle.description}</td>
                    <td>{principle.date}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-info mr-1" onClick={() => openEditModal(principle)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeletePrinciple(principle.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-footer clearfix">
            <ul className="pagination pagination-sm m-0 float-right">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(1)}>First</button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
              </li>
              {pageNumbers.map((number) => (
                <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => paginate(number)}>{number}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(totalPages)}>Last</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Principle</h5>
                <button type="button" className="close" onClick={() => setShowAddModal(false)}>&times;</button>
              </div>
              <div className="modal-body">

  {/* Row 5 */}
  <div className="row mb-3">
    <div className="col-md-6">
      <label className="form-label">Phase</label>
      <select className="form-control" id="phase" name="phase" 
                value={newPrinciple.phase || ""}
                onChange={(e) => setNewPrinciple({ ...newPrinciple, phase: e.target.value })}
                >
                  <option value="All">All</option>
                  <option value="Opening">Opening</option>
                  <option value="Middlegame">Middlegame</option>
                  <option value="Endgame">Endgame</option>
        </select>
    </div>
    <div className="col-md-6">
      <label className="form-label">Title <small className="text-danger">*</small></label>
      <input id="title" name="title" 
                  type="text"
                  className="form-control mb-2"
                  placeholder="Title"
                  value={newPrinciple.title}
                  onChange={(e) => setNewPrinciple({ ...newPrinciple, title: e.target.value })}
                />
      <span className="error-message text-danger" id="pincode-errmsg" />
    </div>
  </div>

  <div className="row mb-3">
  <div className="col-md-12">
      <label className="form-label">Description <small className="text-danger">*</small></label>
      <textarea id="description" name="description" 
                  className="form-control mb-2"
                  placeholder="Description"
                  value={newPrinciple.description}
                  onChange={(e) => setNewPrinciple({ ...newPrinciple, description: e.target.value })}
                />
      <span className="error-message text-danger" id="pincode-errmsg" />
    </div>
  </div>

              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleAddPrinciple}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
{showEditModal && selectedPrinciple && (
  <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
    <div className="modal-dialog modal-lg modal-dialog-scrollable">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Edit Principle</h5>
          <button type="button" className="close" onClick={() => setShowEditModal(false)}>&times;</button>
        </div>
        <div className="modal-body">

          {/* Row 1 */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Phase</label>
              <select
                className="form-control"
                value={selectedPrinciple.phase || ""}
                onChange={(e) =>
                  dispatch(setSelectedPrinciple({
                    ...selectedPrinciple,
                    phase: e.target.value
                  }))
                }
              >
                <option value="All">All</option>
                <option value="Opening">Opening</option>
                <option value="Middlegame">Middlegame</option>
                <option value="Endgame">Endgame</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Title <small className="text-danger">*</small></label>
              <input
                type="text"
                className="form-control"
                placeholder="Title"
                value={selectedPrinciple.title}
                onChange={(e) =>
                  dispatch(setSelectedPrinciple({
                    ...selectedPrinciple,
                    title: e.target.value
                  }))
                }
              />
              <span className="error-message text-danger" />
            </div>
          </div>

          {/* Row 2 */}
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Description <small className="text-danger">*</small></label>
              <textarea
                className="form-control"
                placeholder="Description"
                value={selectedPrinciple.description}
                onChange={(e) =>
                  dispatch(setSelectedPrinciple({
                    ...selectedPrinciple,
                    description: e.target.value
                  }))
                }
              />
              <span className="error-message text-danger" />
            </div>
          </div>

        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Close</button>
          <button className="btn btn-primary" onClick={handleUpdatePrinciple}>Save Changes</button>
        </div>
      </div>
    </div>
  </div>
)}

    </section>
  );
};

export default Principles;
